import { Box, Button, Typography, useTheme } from "@outschool/backpack";
import { Search } from "@outschool/business-rules";
import {
  SearchUseCase,
  SpellingSuggestionType,
} from "@outschool/gql-frontend-generated";
import {
  EAST_ASIA_LOCALES,
  useLocale,
  useTranslation,
} from "@outschool/localization";
import { classComparisonPath } from "@outschool/routes";
import { measurePerformance, useTrackEvent } from "@outschool/ui-analytics";
import { useSession } from "@outschool/ui-auth";
import { TrackedButton } from "@outschool/ui-components-shared";
import {
  SaveSearchButton,
  useSearchFiltersFundingProgramAbbr,
  useSearchQuery,
} from "@outschool/ui-components-website";
import { useServerExperimentContext } from "@outschool/ui-experiments";
import {
  Container,
  TrackingPage,
} from "@outschool/ui-legacy-component-library";
import { Screen, simpleRandomId, useNavigation } from "@outschool/ui-utils";
import ldOmit from "lodash/omit";
import React, { useEffect, useState } from "react";

import { SearchSuggestionsProvider } from "../../../../shared/client/providers/SearchSuggestionsProvider";
import * as Env from "../../../../shared/Env";
import { SearchFilters as SearchFiltersType } from "../../../../shared/generated/graphql";
import * as helpers from "../../../../shared/helpers";
import useClassTrackSearch from "../../../hooks/lib/useClassTrackSearch";
import { useTeacherSearchSuggestions } from "../../../hooks/lib/useTeacherSearchSuggestions";
import { useSpellingSuggestion } from "../../../hooks/SpellingSuggestion";
import { useStickyHeader } from "../../../hooks/useStickyHeader";
import { useSearchFilters } from "../../../lib/useSearchFilters";
import { useTimeZone } from "../../../providers/TimeZoneProvider";
import CompareFooter from "../../classComparison/CompareFooter";
// @ts-ignore
import ClassRequestForm from "../../ClassRequestForm";
import ErrorMessage from "../../ErrorMessage";
import SimpleLayout from "../../layouts/SimpleLayout";
import TeacherHeader from "../../teacher/TeacherHeader";
import TeacherSuggestions from "../../teacher/TeacherSuggestions";
import { uniqueIdFromFilters } from "../common";
import CompareFromSearchButton from "../CompareFromSearchButton";
import EastAsiaSearchToggleButtons from "../EastAsiaSearchToggleButtons";
import ListingsSortBy from "../ListingsSortBy";
import OtherListingsMask from "../OtherListingsMask";
import { addSearchToHistory } from "../RecentSearchHistory";
import SearchToggleButtons, {
  SearchToggleFiltersType,
} from "../SearchToggleButtons";
import ShowMore from "../ShowMore";
import TooSpecific from "../TooSpecific";
import { TopActivityList } from "../TopActivityList";
import useSelectedTeacher from "../useSelectedTeacher";
import { FilterMenuToggle } from "./FilterMenuToggle";
import { LearnerFilterSheetToggle } from "./LearnerFilterSheetToggle";
import { ListingsWrapperMobile } from "./ListingsHelpers";
import MobileSearch from "./MobileSearch";
import { SkeletonCards } from "./MobileSearchListingSkeleton";

export default function MobileSearchListings({
  onlineClassesPageImpressionId,
}: {
  category?: string;
  onlineClassesPageImpressionId?: string;
}) {
  const itemsPerPage = Screen.useIsMobile() ? 6 : 12;
  const { isLoggedIn, currentUserHasLoaded } = useSession();
  const { timeZone } = useTimeZone();
  const { loggedOutUserExperimentUid: anonymousId } =
    useServerExperimentContext();
  const { t } = useTranslation("client\\components\\search\\mobile\\Listings");
  const theme = useTheme();
  const locale = useLocale();
  const trackEvent = useTrackEvent();
  const navigate = useNavigation();

  const [selectedListings, setSelectedListings] = useState<string[]>([]);
  const [isComparing, setIsComparing] = useState<boolean>(false);
  const [showOtherListings, setShowOtherListings] = useState(false);
  const { filters, setFilters } = useSearchFilters();

  const originalSpelling: boolean = filters?.originalSpelling === "yes";
  const { suggestion, loading: suggestionLoading } = useSpellingSuggestion({
    query: filters?.q || "",
    spellingSuggestionType: SpellingSuggestionType.SearchQuery,
  });

  const forceSpellingCorrection =
    !originalSpelling &&
    !suggestionLoading &&
    suggestion?.highConfidence &&
    suggestion?.spellingSuggestion !== null;

  const emKeywords =
    forceSpellingCorrection && suggestion.spellingSuggestion.match(/\s/)
      ? suggestion?.spellingSuggestion
          .split(/\s/)
          .map((keyword: string) =>
            filters?.q?.includes(keyword) ? (
              <>{keyword} </>
            ) : (
              <Typography sx={{ fontWeight: "bold" }}>{keyword} </Typography>
            ),
          )
      : suggestion?.spellingSuggestion;

  const showSpellingSuggestion =
    !originalSpelling &&
    !suggestionLoading &&
    !suggestion?.highConfidence &&
    suggestion?.spellingSuggestion &&
    suggestion?.spellingSuggestion != filters?.q;

  const enableFuzzySearch = !(
    forceSpellingCorrection ||
    showSpellingSuggestion ||
    originalSpelling
  );

  const queryOverride = forceSpellingCorrection
    ? { q: suggestion.spellingSuggestion }
    : {};

  const {
    loading,
    exactListings,
    otherListings,
    exactHasMore,
    exactTotal,
    fetchMore,
    isFetchingMore,
    error,
    exactListingsSearchUid,
    otherListingsSearchUid,
  } = useSearchQuery({
    referrer: document.referrer,
    skip: !currentUserHasLoaded,
    variables: {
      searchFilters: {
        ...ldOmit({ ...filters, ...queryOverride }, [
          "userName",
          "originalSpelling",
        ]),
        timeZone,
      },
      offset: 0,
      limit: itemsPerPage,
      skipRating: false,
      withRecentReviews: false,
      anonymousId,
      pageImpressionId: onlineClassesPageImpressionId,
      isFuzzySearchEnabled: enableFuzzySearch,
      pageLocation: "MOBILE_LISTING",
      useCase: SearchUseCase.Search,
    },
  });

  useClassTrackSearch({
    currentUserHasLoaded,
    exactListings,
    exactTotal,
    itemsPerPage,
    loading: loading,
    searchUid: exactListingsSearchUid,
  });

  const { loading: selectedTeacherLoading, selectedTeacher } =
    useSelectedTeacher(filters?.userUid);

  const availableListings = exactListings;

  useEffect(() => {
    if (
      filters &&
      (((!loading || exactListings.length > 0) && filters.q) ||
        (filters && filters.userUid && filters.userName))
    ) {
      addSearchToHistory(filters);
    }
  }, [loading, exactListings, filters]);

  // When filters change, exit "Compare" mode and clear selected listings
  React.useEffect(() => {
    setIsComparing(false);
    setSelectedListings([]);
  }, [filters]);

  /*
   * Load measurePerformance after listings have loaded
   */
  React.useEffect(() => {
    if (availableListings?.length > 0 && !loading) {
      measurePerformance("os-page-load", {
        source: "search-listings-mobile",
      });
    }
  }, [availableListings, loading]);

  const searchFiltersFundingProgramAbbr =
    useSearchFiltersFundingProgramAbbr(filters);
  const teacherSuggestions = useTeacherSearchSuggestions(
    filters?.q ?? "",
    10,
    searchFiltersFundingProgramAbbr,
  );

  const [needsScroll, setNeedsScroll] = React.useState(false);
  // Scroll to top of the page after applying filters
  useEffect(() => {
    if (needsScroll) {
      setTimeout(() => {
        window.scrollTo(0, 0);
        setNeedsScroll(false);
      }, 0);
    }
  }, [needsScroll]);

  // Sentinel-based auto-fetch (separate from UI)
  const sentinelRef = React.useRef<HTMLDivElement | null>(null);
  const [isFetchingState, setIsFetchingState] = React.useState(false);

  // Auto-scroll to new results
  const [previousListingsCount, setPreviousListingsCount] = React.useState(0);
  const firstNewResultRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    // Only create observer when sentinel exists and it's safe to fetch
    if (
      !sentinelRef.current ||
      !exactHasMore ||
      isFetchingMore ||
      isFetchingState
    ) {
      return;
    }

    const observer = new IntersectionObserver(
      entries => {
        const [entry] = entries;
        if (
          entry.isIntersecting &&
          exactHasMore &&
          !isFetchingMore &&
          !isFetchingState
        ) {
          setIsFetchingState(true);
          try {
            fetchMore();
          } finally {
            // Reset state after a short delay to ensure fetch has started
            setTimeout(() => {
              setIsFetchingState(false);
            }, 100);
          }
        }
      },
      {
        rootMargin: "500px", // Very generous margin for quick response
        threshold: 0.1,
      },
    );

    observer.observe(sentinelRef.current);

    return () => {
      observer.disconnect();
    };
  }, [exactHasMore, isFetchingMore, isFetchingState, fetchMore]);

  // Auto-scroll to new results when they load
  React.useEffect(() => {
    const currentCount = availableListings?.length || 0;

    // If we have more listings than before and we're not on initial load
    if (currentCount > previousListingsCount && previousListingsCount > 0) {
      // Check if the first new result is visible
      if (firstNewResultRef.current) {
        const rect = firstNewResultRef.current.getBoundingClientRect();
        const isVisible = rect.top >= 0 && rect.top <= window.innerHeight;

        // Always scroll to new content to prevent infinite loading loop
        // (skeleton cards would remain visible and trigger another fetch)
        if (!isVisible) {
          setTimeout(() => {
            firstNewResultRef.current?.scrollIntoView({
              behavior: "smooth",
              block: "start",
              inline: "nearest",
            });
          }, 100); // Small delay to ensure DOM is updated
        }
      }
    }

    // Update the count for next comparison
    setPreviousListingsCount(currentCount);
  }, [availableListings?.length, previousListingsCount]);

  const hasTimeFilter = !!(
    filters?.time ||
    filters?.timeOfDay ||
    filters?.startAfter ||
    filters?.endBy ||
    filters?.startAfterTime ||
    filters?.endByTime ||
    filters?.dow
  );

  const tabFilters = Search.getToggleButtonFilters(filters);

  const pageImpressionId = React.useMemo(
    () => onlineClassesPageImpressionId ?? simpleRandomId(),
    [onlineClassesPageImpressionId],
  );

  // set which set of values we prefer
  const performSearch = (insideModal = false) => {
    setNeedsScroll(true);
    // if we are not inside the modal and have not performed a search yet,
    // then manually update the search filters to set the flag showListings=1
    if (!insideModal) {
      // @ts-ignore TS(2345): Argument of type 'ExtendedSearchFiltersType | unde... Remove this comment to see the full error message
      updateSearchFilters("showListings", filters);
    }
  };
  const updateSearchFilters = (
    filterType: string,
    updatedFilters: SearchFiltersType,
  ) => {
    setFilters?.(
      filterType,
      Object.assign(
        {
          showListings: 1,
        },
        filters,
        updatedFilters,
      ),
    );
  };

  const enterCompareMode = () => {
    setIsComparing(true);
    trackEvent("search_open_compare_mode_click");
  };

  const compareClasses = () => {
    navigate(classComparisonPath(selectedListings));
    trackEvent("search_create_comparison_click", {
      activityUids: selectedListings,
    });
  };

  const exitCompareMode = () => {
    setIsComparing(false);
    setSelectedListings([]);
  };

  return (
    <TrackingPage
      name="activityListingsV2"
      pageImpressionIdDefined={pageImpressionId}
      filters={filters}
      // @ts-ignore TS(2345): Argument of type 'ExtendedSearchFiltersType | unde... Remove this comment to see the full error message
      uniqueId={uniqueIdFromFilters("ActivityListingsPageV2", filters)}
    >
      {setImpressionNode => (
        <SimpleLayout
          sx={{ minHeight: "75%", backgroundColor: theme.colors.peacock50 }}
          hideReviewReminder
          hideAccountBanner
          // @ts-ignore TS(2322): Type 'Dispatch<HTMLElement>' is not assignable to ... Remove this comment to see the full error message
          ref={setImpressionNode}
          allowCategoriesNavbar={false}
        >
          <SearchFiltersContainer>
            <Container fullBleed>
              {setFilters && EAST_ASIA_LOCALES.includes(locale) ? (
                <EastAsiaSearchToggleButtons
                  setSearchFilters={setFilters}
                  clearDropdowns={() => {}}
                />
              ) : (
                <SearchToggleButtons
                  searchFilters={tabFilters}
                  onChange={(searchToggleFilters: SearchToggleFiltersType) => {
                    const currentSearchToggle =
                      Search.activeSearchToggleFromFilters(filters || {});

                    // If search toggle needs to change, reset some filters.
                    const newSearchToggleFilters =
                      Search.newFiltersFromSearchToggleChange(
                        currentSearchToggle,
                        searchToggleFilters,
                      );

                    updateSearchFilters("additional", newSearchToggleFilters);
                  }}
                  clearDropdowns={() => {}}
                />
              )}
            </Container>
            <SearchSuggestionsProvider
              initialItem={{
                uid: helpers.uuidv4(),
                // eslint-disable-next-line i18next/no-literal-string
                type: "keyword",
                // @ts-ignore TS(2322): Type 'InputMaybe<string> | undefined' is not assig... Remove this comment to see the full error message
                name: filters?.q,
              }}
            >
              <MobileSearch />
            </SearchSuggestionsProvider>
            {/* Optional flex wrapper to handle the different learner search experiment UI layout */}
            <Box
              flex
              sx={{
                justifyContent: "space-between",
                paddingX: "1em",
                maxHeight: "60px",
                overflow: "wrap",
              }}
            >
              <LearnerFilterSheetToggle />
              <Box
                flex
                sx={{ paddingLeft: "8px", gap: "8px", alignItems: "center" }}
              >
                <FilterMenuToggle
                  // @ts-ignore TS(2322): Type 'ExtendedSearchFiltersType | undefined' is no... Remove this comment to see the full error message
                  searchFilters={filters}
                  updateSearchFilters={updateSearchFilters}
                  performSearch={performSearch}
                  searchUid={exactListingsSearchUid}
                />
                {/* @ts-ignore TS(2322): Type 'ExtendedSearchFiltersType | undefined' is no... Remove this comment to see the full error message */}
                <ListingsSortBy
                  // @ts-ignore TS(2322): Type 'ExtendedSearchFiltersType | undefined' is no... Remove this comment to see the full error message
                  filters={filters}
                  setFilters={setFilters}
                  /* eslint-disable i18next/no-literal-string */
                  sx={{
                    borderRadius: "50%",
                    width: "34px",
                    height: "34px",
                    minWidth: "34px",
                    padding: "4px",
                  }}
                  iconSx={{
                    fontSize: "16px",
                  }}
                  /* eslint-enable i18next/no-literal-string */
                />
                <SaveSearchButton
                  filters={filters}
                  variant="small"
                  /* eslint-disable i18next/no-literal-string */
                  sx={{
                    borderColor: "primary.600",
                    borderRadius: "50%",
                    width: "34px",
                    height: "34px",
                    minWidth: "34px",
                    padding: "4px",
                    fontSize: "16px",
                  }}
                  /* eslint-enable i18next/no-literal-string */
                />
                <CompareFromSearchButton
                  disabled={isComparing || !availableListings.length}
                  onClick={enterCompareMode}
                />
              </Box>
            </Box>
          </SearchFiltersContainer>
          <Container fullBleed backgroundColor={theme.colors.peacock50}>
            <TopActivityList />
            {teacherSuggestions?.teachers.length &&
            !loading &&
            currentUserHasLoaded ? (
              <Box sx={{ paddingBottom: "1em" }}>
                <TeacherSuggestions
                  teacherSuggestions={teacherSuggestions}
                  searchUid={exactListingsSearchUid}
                />
              </Box>
            ) : null}
            {!!selectedTeacher && (
              <Box
                sx={{
                  paddingY: "1em",
                }}
              >
                <TeacherHeader
                  leader={selectedTeacher}
                  includeProfileLink
                  includeShareButton={false}
                />
              </Box>
            )}
            {error ? (
              <div>
                <ErrorMessage
                  value={error}
                  showStatusPageLink={Env.IS_READ_ONLY_MODE}
                />
              </div>
            ) : loading || !currentUserHasLoaded || selectedTeacherLoading ? (
              <SkeletonCards count={6} />
            ) : (
              <Box>
                {showSpellingSuggestion && (
                  <Box
                    sx={{
                      padding: "0.5em",
                    }}
                  >
                    {t`Did you mean: `}
                    <TrackedButton
                      trackingName="spelling_suggestion"
                      trackingTouchEventName="spelling_suggestion_clicked"
                      trackingUniqueId={exactListingsSearchUid}
                      trackingSharedProperties={{
                        ...suggestion,
                        anonymousId,
                      }}
                      sx={{
                        marginTop: "-4px",
                      }}
                      color="primary"
                      size="medium"
                      variant="link"
                      onClick={() =>
                        setFilters?.("q", {
                          q: suggestion?.spellingSuggestion,
                        })
                      }
                    >
                      {suggestion.spellingSuggestion}
                    </TrackedButton>
                  </Box>
                )}
                {forceSpellingCorrection && (
                  <Box
                    sx={{
                      padding: "0.5em",
                    }}
                  >
                    {t`Showing results for: `}
                    <Typography
                      sx={{
                        fontStyle: "italic",
                      }}
                    >
                      {emKeywords}
                    </Typography>
                    <br />
                    {t`Search instead for `}
                    <TrackedButton
                      trackingName="spelling_suggestion"
                      trackingTouchEventName="spelling_suggestion_original_clicked"
                      trackingUniqueId={exactListingsSearchUid}
                      trackingSharedProperties={{
                        ...suggestion,
                        anonymousId,
                      }}
                      sx={{
                        marginTop: "-4px",
                      }}
                      color="primary"
                      size="medium"
                      variant="link"
                      onClick={() =>
                        setFilters?.("q", {
                          q: suggestion.query,
                          originalSpelling: "yes",
                        })
                      }
                    >
                      {suggestion.query}
                    </TrackedButton>
                  </Box>
                )}
                {availableListings?.length ? (
                  <ListingsWrapperMobile
                    listings={availableListings}
                    tracking={{
                      startIndex: 0,
                      filters,
                      searchUid: exactListingsSearchUid,
                      group: "exact_listings",
                    }}
                    firstNewResultIndex={previousListingsCount}
                    onFirstNewResultRef={ref => {
                      firstNewResultRef.current = ref;
                    }}
                    isComparing={isComparing}
                    selectedListings={selectedListings}
                    setSelectedListings={setSelectedListings}
                  />
                ) : null}
                {/* Show single skeleton card when fetching more results */}
                {(isFetchingMore || isFetchingState) &&
                  availableListings?.length > 0 && (
                    <Box sx={{ marginBottom: "1em" }}>
                      <SkeletonCards count={1} />
                    </Box>
                  )}
                {/* Invisible sentinel element - triggers fetch when visible */}
                {exactHasMore &&
                  !isFetchingMore &&
                  !isFetchingState &&
                  availableListings?.length > 0 && (
                    <div
                      ref={sentinelRef}
                      style={{
                        height: "1px",
                        width: "100%",
                        backgroundColor: "transparent",
                      }}
                      aria-hidden="true"
                    />
                  )}
                {otherListings && otherListings.length > 0 && (
                  <Box
                    flex
                    sx={{
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      textAlign: "center",
                      height: "32rem",
                    }}
                  >
                    {availableListings?.length > 0 ? (
                      <ShowMore
                        hasResults
                        hasTimeFilter={hasTimeFilter}
                        disabled={showOtherListings}
                        onClick={() => setShowOtherListings(true)}
                      />
                    ) : otherListings?.length > 0 ? (
                      <ShowMore
                        hasResults={false}
                        hasTimeFilter={hasTimeFilter}
                        disabled={showOtherListings}
                        onClick={() => setShowOtherListings(true)}
                      />
                    ) : (
                      <TooSpecific />
                    )}
                  </Box>
                )}
                {otherListings && otherListings.length > 0 && (
                  <OtherListingsMask hidden={!showOtherListings}>
                    <ListingsWrapperMobile
                      listings={otherListings}
                      tracking={{
                        startIndex: 0,
                        filters,
                        searchUid: otherListingsSearchUid,
                        group: "other_listings",
                      }}
                      disabled={!showOtherListings}
                      isComparing={isComparing}
                      selectedListings={selectedListings}
                      setSelectedListings={setSelectedListings}
                    />
                  </OtherListingsMask>
                )}
                {/* Show "Request a Class" section only when there are no more results to fetch */}
                {availableListings?.length > 0 &&
                  !exactHasMore &&
                  !isFetchingMore && (
                    <Box
                      sx={{
                        textAlign: "center",
                        position: "relative",
                        width: "100%",
                        maxWidth: "1024",
                        margin: "0 auto",
                        padding: "0 1em",
                        paddingTop: "3em",
                        paddingBottom: "3em",
                      }}
                    >
                      {t`Looking for something different?`}
                      <Box
                        sx={{
                          paddingY: "0.75em !important",
                          marginBottom: "1em",
                        }}
                      >
                        <ClassRequestForm
                          isLoggedIn={isLoggedIn}
                          renderButton={(open: $TSFixMe) => (
                            <Button onClick={open}>{t`Request a Class`}</Button>
                          )}
                        />
                      </Box>
                    </Box>
                  )}
              </Box>
            )}
          </Container>
          {isComparing && (
            <CompareFooter
              selectedListings={selectedListings}
              onClickCompare={compareClasses}
              onClickCancelCompare={exitCompareMode}
            />
          )}
        </SimpleLayout>
      )}
    </TrackingPage>
  );
}
const SearchFiltersContainer = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { enabled: mainHeaderStickyEnabled, headerHeight: mainHeaderHeight } =
    useStickyHeader();
  const theme = useTheme();

  return (
    <Box
      sx={{
        backgroundColor: theme.colors.peacock50,
        position: "sticky",
        top: mainHeaderStickyEnabled ? mainHeaderHeight : 0,
        zIndex: 200,
        padding: "1em 0 1em",
      }}
    >
      {children}
    </Box>
  );
};
